import React, { useContext, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { icons } from "../../../constants";
import { TranslationsContext } from "../../../utils/TranslationsContext";
import ExpandableText from "./ExpandableText";
import { useTheme } from "../../../utils/ThemeContext";
import { useNavigate } from "react-router-dom";
import IconWithTooltip from "../../IconWithTooltip/IconWithTooltip";
import AvatarThumb from "../../AvatarThumb/AvatarThumb";

const TableRow = ({
  rowData,
  totalWidth,
  onClick,
  onDownloadClick,
  seeAllColumns,
  isPlaceholder,
  isLoadingData,
  query,
  isLast,
  onDeleteClick,
  compact,
}) => {
  const { t } = useContext(TranslationsContext);
  const { theme } = useTheme();
  const navigate = useNavigate();

  let status = "";
  if (rowData.status) {
    switch (rowData.status.toLowerCase()) {
      case "odobren":
        status = t("APPROVED");
        break;
      case "odbijen":
        status = t("REJECTED");
        break;
      case "processing":
        status = t("PROCESSING");
        break;
      case "nov":
        status = t("NEW");
        break;
      case "prijavljen":
        status = t("NEW");
        break;
      default:
        status = t("NEW");
        break;
    }
  }

  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      // Ako se pravi višak visine, znači da je tekst skraćen
      setIsTruncated(el.scrollHeight > el.clientHeight);
    }
  }, []);

  const handleToggle = () => setExpanded((prev) => !prev);

  // Mobile compact rows collapse each column into two lines instead of the
  // narrow, horizontally-scrolled desktop columns. Skeleton shape is
  // type-agnostic since the placeholder rowData (from TableFirst) is a
  // positional array of column widths, not a keyed record with a className.
  const renderCompactPlaceholder = () => (
    <>
      <div className="table-row-compact-line1">
        <div className="animated-bg-text animated-bg compact-skeleton-bar compact-skeleton-bar--wide" />
      </div>
      <div className="table-row-compact-line2">
        <div className="animated-bg-text animated-bg compact-skeleton-bar compact-skeleton-bar--narrow" />
      </div>
    </>
  );

  const renderCompactEventRow = () => (
    <>
      <div className="table-row-compact-line1">
        <ExpandableText
          text={String(rowData.naziv)}
          isLoadingData={isLoadingData}
          type="link"
          to={`/event/${rowData.slug || rowData.id}`}
          className="first-events naziv table-row-compact-title"
          query={query}
        />
      </div>
      <div className="table-row-compact-line2">
        <p className="inria-sans-400 font-12 table-row-compact-line2-text">
          {`${rowData.trajanje_od} - ${rowData.trajanje_do}`}
        </p>
        <p className="inria-sans-400 font-12 table-row-compact-line2-metric">
          {String(rowData.procesuirano_obradjeno)}
        </p>
      </div>
    </>
  );

  // Avatar and the name/position text are flex siblings in one row, with
  // name+position stacked in their own column — so line1/line2 share the
  // exact same left edge structurally, not by matching a margin to the
  // avatar's width (which drifts the moment either one changes).
  const renderCompactPersonRow = () => (
    <div className="table-row-compact-person">
      <AvatarThumb id={rowData.id} alt="img" size={36} borderRadius={6} />
      <div className="table-row-compact-person-info">
        <div className="table-row-compact-line1">
          <p className="inria-sans-400 font-14 table-row-compact-title">
            {String(rowData.ime)}
          </p>
          <div
            className={`first-registered-user status ${
              (rowData.status || "").toLowerCase()
            } table-row-compact-status`}
          >
            <p className="inria-sans-400 font-12">{status}</p>
          </div>
        </div>
        <div className="table-row-compact-line2">
          <p className="inria-sans-400 font-12 table-row-compact-line2-text">
            {String(rowData.pozicija)}
          </p>
          {rowData.status?.toLowerCase() === "odobren" && rowData.accreditation && (
            <IconWithTooltip tooltip={t("DOWNLOAD")}>
              <img
                className="pointer"
                alt="download"
                src={theme == "light" ? icons.download : icons.download_dark}
                onClick={() => onDownloadClick(rowData.id)}
              />
            </IconWithTooltip>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`flex-direction-row items-center width-100 table-row ${
        isLast ? "last-row" : ""
      } ${compact ? "compact" : ""}`}
      onClick={(e) => {
        const linkElement =
          e.target.tagName === "A" ? e.target : e.target.closest("a");
        if (linkElement) {
          return;
        }

        if (rowData.className === "first-registered-user") {
          e.stopPropagation();
          onClick(rowData.id);
        } else if (rowData.className === "first-events") {
          navigate(`/event/${rowData.slug || rowData.id}`);
        }
      }}
    >
      {compact ? (
        isPlaceholder ? (
          renderCompactPlaceholder()
        ) : rowData.className === "first-events" ? (
          renderCompactEventRow()
        ) : (
          renderCompactPersonRow()
        )
      ) : (
        Object.entries(rowData)?.map(([key, value]) => {
        // trajanje_do isn't a real column (not in eventsTableOptionsFirst) —
        // it's only added to rowData for the compact mobile row's date
        // range, and has no entry in rowData.width, so it must be skipped
        // here or the desktop path would render it as a broken NaN-width cell.
        if (key === "width" || key === "id" || key === "slug" || key === "className" || key === "trajanje_do") return null;

        let columnWidth =
          (isPlaceholder
            ? value.width / totalWidth
            : rowData.width[key.toLowerCase()] / totalWidth) * 100;

        return (
          <React.Fragment key={key}>
            {isPlaceholder ? (
              <div
                className={`animated-bg-text animated-bg placeholder-cell ${
                  seeAllColumns ? "" : "hide"
                } ${value.className}`}
                style={{
                  height: "20px",
                  margin: "0 6px",
                  minWidth: "108px",
                  width: `${columnWidth}%`,
                }}
              ></div>
            ) : key === "accreditation" ? null : (
              <div
                key={key}
                style={{ width: `${columnWidth}%` }}
                className={`flex-direction-row items-center table-cell ${
                  rowData.className ? rowData.className : ""
                } ${key === "status" ? `${key} ${value.toLowerCase()}` : key} ${
                  seeAllColumns ? "" : "hide"
                }`}
              >
                {rowData.className === "first-events" && key === "naziv" ? (
                  <ExpandableText
                    text={String(value)}
                    isLoadingData={isLoadingData}
                    isPlaceholder={isPlaceholder}
                    type={"link"}
                    to={`/event/${rowData.slug || rowData.id}`}
                    className={rowData.className + " " + key + " " + value}
                    query={query}
                  />
                ) : key === "ime" ? (
                  <p
                    className="inria-sans-400 font-14 pointer"
                    onClick={() => onClick(rowData.id)}
                  >
                    {String(value)}
                  </p>
                ) : key === "download" &&
                  rowData.status.toLowerCase() === "odobren" &&
                  rowData.accreditation ? (
                  <IconWithTooltip tooltip={t("DOWNLOAD")}>
                    <img
                      className="pointer"
                      alt="download"
                      src={
                        theme == "light" ? icons.download : icons.download_dark
                      }
                      onClick={() => onDownloadClick(rowData.id)}
                    />
                  </IconWithTooltip>
                ) : key === "slika_avatar" ? (
                  <AvatarThumb id={rowData.id} alt="img" size={40} borderRadius={6} />
                ) : (
                  <ExpandableText
                    text={key !== "status" ? String(value) : String(status)}
                    isLoadingData={isLoadingData}
                    isPlaceholder={isPlaceholder}
                    className={rowData.className + " " + key + " " + value}
                    query={query}
                  />
                )}
              </div>
            )}
          </React.Fragment>
        );
        })
      )}
    </div>
  );
};

export default TableRow;
